import { useNavigate } from "react-router-dom";

import useStateHandler from "@/hooks/useStateHandler";

const ARTWORK_ROUTING_DEBUG_KEY = "artworkRouting";

function isArtworkRoutingDebugEnabled() {
  if (typeof window === "undefined" || !window.SuarteDebug) {
    return false;
  }
  const config = window.SuarteDebug[ARTWORK_ROUTING_DEBUG_KEY];
  if (config === true) {
    return true;
  }
  if (config && typeof config === "object") {
    if (typeof config.enabled === "boolean") {
      return config.enabled;
    }
    return true;
  }
  return false;
}

function normalizeSource(maybeSource, context) {
  const source = typeof maybeSource === "function" ? maybeSource(context) : maybeSource;

  if (!source || typeof source !== "object") {
    return null;
  }

  const normalized = {
    type: source.type,
    label: typeof source.label === "string" ? source.label : source.title,
    payload: source.payload,
    continuation: source.continuation,
  };

  if (
    normalized.label === undefined
    && normalized.type === undefined
    && normalized.payload === undefined
    && normalized.continuation === undefined
  ) {
    return null;
  }

  if (typeof normalized.label === "string") {
    normalized.label = normalized.label.trim();
  }

  return normalized;
}

function normalizeArtworkSourceForItems(source) {
  if (!source || typeof source !== "object") {
    return null;
  }

  const normalized = {
    type: source.type,
    label: typeof source.label === "string" ? source.label : "",
    continuation: source.continuation,
  };

  if (typeof normalized.label === "string") {
    normalized.label = normalized.label.trim();
  }

  return normalized;
}

function stampArtworkViewSource(payload, source = null) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const normalizedSource = normalizeArtworkSourceForItems(source);

  const stampItem = (item) => {
    if (!item || typeof item !== "object") {
      return item;
    }
    let nextItem = item;

    if (normalizedSource) {
      const currentSource = item._artworkViewSource;
      const hasSameSource =
        currentSource
        && currentSource.type === normalizedSource.type
        && currentSource.label === normalizedSource.label;

      if (!hasSameSource) {
        nextItem = { ...nextItem, _artworkViewSource: normalizedSource };
      }
    }

    return nextItem;
  };

  const clone = { ...payload };

  if (Array.isArray(clone.data)) {
    clone.data = clone.data.map(stampItem);
    return clone;
  }

  if (clone.data && typeof clone.data === "object" && Array.isArray(clone.data.data)) {
    clone.data = {
      ...clone.data,
      data: clone.data.data.map(stampItem),
    };
    return clone;
  }

  return clone;
}

export default function useNavigateToArtwork(cacheKey, artworks, endpoint = "", sourceMeta) {
  const { cacheHandler } = useStateHandler();
  const navigate = useNavigate();

  function logArtworkNavigate(event, payload = {}) {
    if (!isArtworkRoutingDebugEnabled()) {
      return;
    }
    console.log(`[ARTWORK_ROUTING][navigate] ${event}`, {
      at: new Date().toISOString(),
      cacheKey,
      endpoint,
      artworksLength: artworks?.data?.length ?? 0,
      ...payload,
    });
  }

  function navigateToArtwork(slug, overrideSourceOrOptions, navigationOptions) {
    if (!slug) {
      logArtworkNavigate("skip-empty-slug");
      return;
    }

    let overrideSource = overrideSourceOrOptions;
    let extraOptions = navigationOptions;

    if (
      extraOptions === undefined
      && overrideSourceOrOptions
      && typeof overrideSourceOrOptions === "object"
      && !Array.isArray(overrideSourceOrOptions)
      && (
        "query" in overrideSourceOrOptions
        || "viewContext" in overrideSourceOrOptions
        || "overrideSource" in overrideSourceOrOptions
      )
    ) {
      extraOptions = overrideSourceOrOptions;
      overrideSource = overrideSourceOrOptions.overrideSource;
    }

    const queryParams = new URLSearchParams();
    const viewContext = extraOptions?.viewContext ?? null;
    const explicitQuery = extraOptions?.query ?? null;

    if (explicitQuery && typeof explicitQuery === "object") {
      Object.entries(explicitQuery).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }
        queryParams.set(key, `${value}`);
      });
    }

    const initialContext = { slug, cacheKey, endpoint, artworks };
    const normalizedSource = normalizeSource(
      overrideSource ?? extraOptions?.overrideSource ?? sourceMeta,
      initialContext
    );
    const artworksToStore = artworks ? stampArtworkViewSource(artworks, normalizedSource) : artworks;

    cacheHandler.storeInCache(`artworks_of_${cacheKey}`, { ...artworksToStore, endpoint }, {
      invalidateWhen: ["EDIT_ARTWORK", "DELETE_ARTWORK", "BUY_ARTWORK"]
    });

    const state = {
      from: true,
      cacheKey: `artworks_of_${cacheKey}`
    };

    if (normalizedSource) {
      state.artworkViewSource = normalizedSource;
    }

    if (viewContext && typeof viewContext === "object") {
      state.artworkViewContext = viewContext;
    }

    let path = `/artwork/${slug}`;
    const queryString = queryParams.toString();
    if (queryString.length > 0) {
      path = `${path}?${queryString}`;
    }

    logArtworkNavigate("navigate-to-artwork", {
      slug,
      path,
      stateCacheKey: state.cacheKey,
      hasViewContext: Boolean(state.artworkViewContext),
    });
    navigate(path, { state });
  }

  return navigateToArtwork;
}
